import { BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useBCMSContext, useStateManager } from "@bcm-dev/bcms-vite";
import React from "react";

import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { PrimaryButton, SecondaryButton } from "components/ui";
import Classes, { ACHTemplate, UserAccount } from "model";
import UsersListWidget from "./components/UsersListWidget";
import "./styles.css";
import "./mobile.css";


/**
 * @function DenyUsersPage
 * @return {React.Component}
 * @exported true
 * @description The main component for DenyUsersPage
 */
export default function DenyUsersPage(props) {
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);

    const data = { ...getDefaultPageData(), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };

    // START: Functions for setting the "deny access" values for a user or users,
    // which is what puts "x"s on the boxes.
    const updateACHTemplateDenyUserAccessIDs = (type, acc, item, achTemplateID, id) => {
        if(type === "push") {
            if(item.id === achTemplateID) {
                const ids = [ ...item.denyAccessToUserIDs, `${ id }` ];
                const res = ACHTemplate.init({ ...item.record, denyAccessToUserIDs: ids });
                acc.push(res);
            } else {
                acc.push(item);
            }
        } else if(type === "filter") {
            if(item.id === achTemplateID) {
                const ids = item.denyAccessToUserIDs.filter(_item => _item !== id);
                const userAccount = ACHTemplate.init({ ...item.record, denyAccessToUserIDs: ids });
                acc.push(userAccount);
            } else {
                acc.push(item);
            }
        }
    }

    const updateSingleACHTemplateAccessIDs = (type, achTemplates, achTemplateID) => {
        const allUserAccountIDs = UserAccount.initAll().reduce((_acc, _item) => {
            _acc.push(_item.id);
            return _acc;
        }, []);

        if(type === "pushAll" || type === "filterAll") {
            return achTemplates.reduce((acc, item) => {
                if(item.id === achTemplateID) {
                    const ids = type === "pushAll" ? [ ...allUserAccountIDs ] : [];
                    const achTemplate = ACHTemplate.init({ ...item.record, denyAccessToUserIDs: ids });
                    acc.push(achTemplate);
                } else {
                    acc.push(item);
                }
                return acc;
            }, []);
        } 
    }
    const updateAllACHTemplateAccessIDs = (type, achTemplates, userAccountID) => {
        // Will either "pushAll" or "filterAll".
        return achTemplates.reduce((acc, item) => {
            const currIDs = item.denyAccessToUserIDs.filter(item => item !== userAccountID);
            const ids = type === "pushAll" ? [ ...currIDs, userAccountID ] : [ ...currIDs ];
            const achTemplate = ACHTemplate.init({ ...item.record, denyAccessToUserIDs: ids });
            acc.push(achTemplate);
            return acc;
        }, []);
    }
    // END: Functions for setting the "deny access" values for a user or users,
    // which is what puts "x"s on the boxes.

    // Controller for the page.
    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        // TODO: on change, set user access back to global default.
        onACHTemplatesChange: val => {
            let selectedACHTemplateID = data.selectedACHTemplateID;

            if(selectedACHTemplateID === undefined && val === "Select a Template") {
                return;
            }

            if(val === "Select a Template") {
                selectedACHTemplateID = undefined;
            } else {
                selectedACHTemplateID = val;
            }

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "achTemplates" ], ACHTemplate.initAll())
            .update([ "data", "userAccounts" ], UserAccount.initAll())
            .update([ "data", "selectedACHTemplateID" ], selectedACHTemplateID)
            .update([ "data", "selectedUserAccountID" ], undefined)
            .submit(setState);
        },

        // TODO: on change, set user access back to global default.
        onUserAccountChange: val => {
            let selectedUserAccountID = data.selectedUserAccountID;

            if(selectedUserAccountID === undefined && val === "Select a User") {
                return;
            }

            if(val === "Select a User") {
                selectedUserAccountID = undefined;
            } else {
                selectedUserAccountID = val;
            }
            
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "achTemplates" ], ACHTemplate.initAll())
            .update([ "data", "userAccounts" ], UserAccount.initAll())
            .update([ "data", "selectedUserAccountID" ], selectedUserAccountID)
            .update([ "data", "selectedACHTemplateID" ], undefined)
            .submit(setState);
        },

        onCheckboxClick: obj => val => {
            let selectedACHTemplateID = data.selectedACHTemplateID;
            let selectedUserAccountID = data.selectedUserAccountID;
            let userAccounts = data.userAccounts;
            let achTemplates = data.achTemplates;

            if(obj instanceof UserAccount) {
                achTemplates = achTemplates.reduce((acc, item) => {
                    updateACHTemplateDenyUserAccessIDs(val == true ? "push" : "filter", acc, item, selectedACHTemplateID, obj.id);
                    return acc;
                }, []);
            } else if(obj instanceof ACHTemplate) {
                achTemplates = achTemplates.reduce((acc, item) => {
                    updateACHTemplateDenyUserAccessIDs(val == true ? "push" : "filter", acc, item, obj.id, selectedUserAccountID);
                    return acc;
                }, []);
            }

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "achTemplates" ], achTemplates)
            .update([ "data", "userAccounts" ], userAccounts)
            .update([ "data", "selectedUserAccountID" ], selectedUserAccountID)
            .submit(setState);
        },

        onDenyAllClick: obj => val => {
            let achTemplates = data.achTemplates;
            let userAccounts = data.userAccounts;
            let selectedUserAccountID = data.selectedUserAccountID;
            let selectedACHTemplateID = data.selectedACHTemplateID;
            
            if(selectedACHTemplateID) {
                achTemplates = updateSingleACHTemplateAccessIDs(val == true ? "pushAll" : "filterAll", data.achTemplates, obj.id);
            } else if(selectedUserAccountID) {
                achTemplates = updateAllACHTemplateAccessIDs(val == true ? "pushAll" : "filterAll", data.achTemplates, obj.id);
            }

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "achTemplates" ], achTemplates)
            .update([ "data", "userAccounts" ], userAccounts)
            .update([ "data", "selectedUserAccountID" ], selectedUserAccountID)
            .submit(setState);
        },

        onCancel: () => {
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "achTemplates" ], ACHTemplate.initAll())
            .update([ "data", "userAccounts" ], UserAccount.initAll())
            .update([ "data", "selectedACHTemplateID" ], undefined)
            .update([ "data", "selectedUserAccountID" ], undefined)
            .submit(setState);
        }
    });

    return (
        <PageContainer className="deny-users-page" state={ { data, ui } } controller={ controller }>
            <PageContent />
        </PageContainer>
    )
}

export const getDefaultPageData = controller => {
    //BCMSRecord.globalControllerFor(Classes, controller);
    
    let _achTemplates = ACHTemplate.initAll();
    let _userAccounts = UserAccount.initAll();

    // Return the default workflow state.
    return {
        achTemplates: _achTemplates,
        userAccounts: _userAccounts,
        selectedACHTemplateID: undefined,
        selectedUserAccountID: undefined
    };
};

export const getDefaultPageUI = () => ({
    selectATemplateDropdownIsActive: undefined,
    selectAUserDropdownIsActive: undefined
});

function PageContent(props) {
    const { data, ui, onCheckboxClick, onDenyAllClick, onACHTemplatesChange, onUserAccountChange, onCancel } = useStateManager();
    const selectedACHTemplateID = data.selectedACHTemplateID ?? "Select a Template";
    const selectedUserAccountID = data.selectedUserAccountID ?? "Select a User";
    const selectATemplateDropdownIsActiveProp = ui.selectATemplateDropdownIsActive === "true" ? { active: ui.selectATemplateDropdownIsActive } : {};
    const selectAUserDropdownIsActiveProp = ui.selectAUserDropdownIsActive === "true" ? { active: ui.selectAUserDropdownIsActive } : {};

    const achTemplateItems = data.achTemplates.reduce((acc, item, i) => {
        if(i === 0) {
            acc.push(<CustomSelectItem key="prompt" value="Select a Template">Select a Template</CustomSelectItem>);
            acc.push(<CustomSelectItem key={ item.id } value={ item.id }>{ item.name }</CustomSelectItem>);
        } else {
            acc.push(<CustomSelectItem key={ item.id } value={ item.id }>{ item.name }</CustomSelectItem>);
        }
        return acc;
    }, []);

    const userAccountItems = data.userAccounts.reduce((acc, item, i) => {
        const fullName = UserAccount.userIsCurrentUser(item) ? `${ item.fullName } (You)` : item.fullName;

        if(i === 0) {
            acc.push(<CustomSelectItem key="prompt" value="Select a User">Select a User</CustomSelectItem>);
            acc.push(<CustomSelectItem key={ item.id } value={ item.id }>{ `${ fullName }` }</CustomSelectItem>);
        } else {
            acc.push(<CustomSelectItem key={ item.id } value={ item.id }>{ `${ fullName }` }</CustomSelectItem>);
        }
        return acc;
    }, []);

    return (
        <PageContentView>
            <CenterContentContainer>
                <ContentHeaderView text="Deny ACH Template Access - Mass Update" />
                <FormContainer className="small-form-width">
                    <FormRowView className="select-a-template-row">
                        <CustomSelect value={ selectedACHTemplateID } onChange={ onACHTemplatesChange } { ...selectATemplateDropdownIsActiveProp } dataPoint="Select_A_Template_Select">
                            { achTemplateItems }
                        </CustomSelect>
                    </FormRowView>
                    <FormRowView className="select-a-user-row">
                        <p>OR</p>
                        <CustomSelect value={ selectedUserAccountID } onChange={ onUserAccountChange } { ...selectAUserDropdownIsActiveProp } dataPoint="Select_A_User_Select">
                            { userAccountItems }
                        </CustomSelect>
                    </FormRowView>
                    { data.selectedACHTemplateID && 
                        <FormRowView className="users-row">
                            <UsersListWidget userAccounts={ data.userAccounts } achTemplates={ data.achTemplates } selectedACHTemplateID={ selectedACHTemplateID }
                             onCheckboxClick={ onCheckboxClick } onDenyAllClick={ onDenyAllClick } />
                        </FormRowView>
                    }
                    { data.selectedUserAccountID && 
                        <FormRowView className="templates-row">
                            <UsersListWidget userAccounts={ data.userAccounts } achTemplates={ data.achTemplates } selectedUserAccountID={ selectedUserAccountID }
                             onCheckboxClick={ onCheckboxClick } onDenyAllClick={ onDenyAllClick }/>
                        </FormRowView>
                    }
                    <FormButtonRow>
                        <SecondaryButton text="Cancel" onClick={ onCancel } />
                        <DisabledPrompt>
                            <PrimaryButton text="Submit" />
                        </DisabledPrompt>
                    </FormButtonRow>
                </FormContainer>
            </CenterContentContainer>
        </PageContentView>
    );
}
